import React, { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * Settings > AI Connections — self-service MCP connections.
 * Thin UI over /api/v1/mcp/connections (McpConnectionsController):
 * list/create/revoke connection tokens (scope-capped server-side by the
 * user's own permissions) and view each connection's audit activity.
 * Task: eden-api-mcp-ai-2026-07-10.
 */

function csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? '';
}

async function api(method, url, body) {
    const response = await fetch(url, {
        method,
        credentials: 'same-origin',
        headers: {
            Accept: 'application/json',
            'X-CSRF-TOKEN': csrfToken(),
            ...(body ? { 'Content-Type': 'application/json' } : {}),
        },
        body: body ? JSON.stringify(body) : undefined,
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
        const message = payload?.error?.message
            ?? (payload?.error?.fields ? Object.values(payload.error.fields).flat().join(' ') : null)
            ?? `Request failed (${response.status})`;
        throw new Error(message);
    }
    return payload;
}

/** Friendly names for scopes; falls back to the raw slug. */
const SCOPE_NAMES = {
    'mcp:crm.read': 'CRM contacts',
    'mcp:crm.write': 'CRM contacts',
    'mcp:forms.read': 'Forms & entries',
    'mcp:forms.write': 'Forms & entries',
    'mcp:lists.read': 'Lists',
    'mcp:lists.write': 'Lists',
    'mcp:pages.read': 'Pages',
    'mcp:pages.write': 'Pages',
    'mcp:reports.read': 'Reports',
    'mcp:tasks.read': 'Tasks',
    'mcp:tasks.write': 'Tasks',
    'mcp:emails.read': 'Email templates',
    'mcp:emails.write': 'Email templates',
    'mcp:automations.read': 'Automations',
    'mcp:automations.write': 'Automations',
    'mcp:workflows.read': 'Workflows',
    'mcp:workflows.write': 'Workflows',
    'mcp:journeys.read': 'Journeys',
    'mcp:journeys.write': 'Journeys',
    'mcp:notifications.read': 'Notifications',
    'mcp:notifications.write': 'Send notifications',
    'mcp:campaigns.read': 'Campaigns',
    'mcp:campaigns.write': 'Campaigns',
    'mcp:files.read': 'Files & folders',
    'mcp:search.read': 'Search everything',
    'mcp:integrations.read': 'Integrations',
    'mcp:uploads.write': 'File uploads',
    'mcp:debug.read': 'Debug telemetry (admin)',
    'mcp:admin.read': 'Permissions admin (admin)',
    'mcp:admin.write': 'Permissions admin (admin)',
};

function scopeLabel(scope) {
    return SCOPE_NAMES[scope] ?? scope.replace(/^mcp:/, '').replace('.', ' — ');
}

/**
 * Self-contained toggle pill: the app theme hides raw checkbox inputs, so
 * these use only inline styles — always visible, obviously clickable.
 */
function ScopePill({ scope, selected, onToggle }) {
    return (
        <button
            type="button"
            onClick={() => onToggle(scope)}
            aria-pressed={selected}
            title={scope}
            style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                margin: '3px 0',
                padding: '6px 10px',
                borderRadius: 5,
                border: selected ? '2px solid #337ab7' : '1px solid #ccc',
                background: selected ? '#eaf2fb' : '#fff',
                color: '#333',
                cursor: 'pointer',
                fontSize: 13,
                lineHeight: 1.3,
            }}
        >
            <span
                style={{
                    display: 'inline-block',
                    width: 16,
                    height: 16,
                    marginRight: 8,
                    verticalAlign: 'text-bottom',
                    borderRadius: 3,
                    border: selected ? 'none' : '1px solid #999',
                    background: selected ? '#337ab7' : '#fff',
                    color: '#fff',
                    textAlign: 'center',
                    fontSize: 12,
                    fontWeight: 700,
                }}
            >
                {selected ? '✓' : ''}
            </span>
            {scopeLabel(scope)}
        </button>
    );
}

/**
 * Clipboard write that also works on non-secure origins (e.g. http://taskjar
 * in dev), where navigator.clipboard is undefined: falls back to a hidden
 * textarea + execCommand. Returns true on success.
 */
async function copyText(text) {
    try {
        if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(text);
            return true;
        }
    } catch {
        /* fall through to the legacy path */
    }
    try {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(textarea);
        return ok;
    } catch {
        return false;
    }
}

export default function AiConnections() {
    const [connections, setConnections] = useState([]);
    const [allowedScopes, setAllowedScopes] = useState([]);
    const [endpoint, setEndpoint] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [label, setLabel] = useState('');
    const [ttlDays, setTtlDays] = useState(30);
    const [selectedScopes, setSelectedScopes] = useState([]);
    const [creating, setCreating] = useState(false);
    const [newToken, setNewToken] = useState(null);

    const [activityFor, setActivityFor] = useState(null);
    const [activity, setActivity] = useState([]);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const payload = await api('GET', '/api/v1/mcp/connections');
            setConnections(payload.data ?? []);
            setAllowedScopes(payload.meta?.allowed_scopes ?? []);
            setEndpoint(payload.meta?.mcp_endpoint ?? '');
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const scopeGroups = useMemo(() => {
        const reads = allowedScopes.filter((s) => s.endsWith('.read'));
        const writes = allowedScopes.filter((s) => s.endsWith('.write'));
        return { reads, writes };
    }, [allowedScopes]);

    const toggleScope = (scope) => {
        setSelectedScopes((current) => (
            current.includes(scope)
                ? current.filter((s) => s !== scope)
                : [...current, scope]
        ));
    };

    const create = async (event) => {
        event.preventDefault();
        setCreating(true);
        setError(null);
        setNewToken(null);
        try {
            const payload = await api('POST', '/api/v1/mcp/connections', {
                label,
                scopes: selectedScopes,
                ttl_days: Number(ttlDays) || undefined,
            });
            setNewToken(payload.data);
            setLabel('');
            setSelectedScopes([]);
            await load();
        } catch (e) {
            setError(e.message);
        } finally {
            setCreating(false);
        }
    };

    const revoke = async (tokenId) => {
        if (!window.confirm('Revoke this connection? The assistant will immediately lose access.')) {
            return;
        }
        setError(null);
        try {
            await api('DELETE', `/api/v1/mcp/connections/${tokenId}`);
            if (activityFor === tokenId) {
                setActivityFor(null);
            }
            await load();
        } catch (e) {
            setError(e.message);
        }
    };

    const showActivity = async (tokenId) => {
        if (activityFor === tokenId) {
            setActivityFor(null);
            return;
        }
        setError(null);
        try {
            const payload = await api('GET', `/api/v1/mcp/connections/${tokenId}/activity`);
            setActivity(payload.data ?? []);
            setActivityFor(tokenId);
        } catch (e) {
            setError(e.message);
        }
    };

    const [copied, setCopied] = useState(null); // 'token' | 'config' | null

    const copy = async (text, which) => {
        const ok = await copyText(text);
        setCopied(ok ? which : 'failed');
        setTimeout(() => setCopied(null), 2500);
    };

    const clientConfigJson = (token) => JSON.stringify({
        mcpServers: {
            eden: {
                type: 'http',
                url: endpoint,
                headers: {
                    Authorization: `Bearer ${token.access_token}`,
                    'X-Business-Template-Id': String(token.business_template_id),
                },
            },
        },
    }, null, 2);

    return (
        <div className="ai-connections">
            {error && <div className="alert alert-danger">{error}</div>}

            <div className="panel panel-default with-rounded-corner">
                <div className="panel-heading"><h4>MCP endpoint</h4></div>
                <div className="panel-body">
                    <p>
                        Point your AI assistant (Claude, ChatGPT, Cursor, VS Code…) at this
                        endpoint with a connection token below. Full instructions:{' '}
                        <code>documents/guides/mcp_connect_ai_assistants.md</code>
                    </p>
                    <pre style={{ userSelect: 'all' }}>{endpoint || '(configure MCP_PUBLIC_PROTOCOL_URL)'}</pre>
                </div>
            </div>

            <div className="panel panel-default with-rounded-corner">
                <div className="panel-heading"><h4>Connect a new assistant</h4></div>
                <div className="panel-body">
                    <form onSubmit={create}>
                        <div className="row">
                            <div className="col-sm-4">
                                <div className="form-group">
                                    <label>Name</label>
                                    <input
                                        className="form-control"
                                        value={label}
                                        onChange={(e) => setLabel(e.target.value)}
                                        placeholder="e.g. Claude Desktop"
                                        maxLength={60}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Expires after (days)</label>
                                    <input
                                        type="number"
                                        min="1"
                                        max="90"
                                        className="form-control"
                                        value={ttlDays}
                                        onChange={(e) => setTtlDays(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="col-sm-4">
                                <label>Can read <small className="text-muted">(view data)</small></label>{' '}
                                <button
                                    type="button"
                                    className="btn btn-link btn-xs"
                                    onClick={() => setSelectedScopes((current) => [...new Set([...current, ...scopeGroups.reads])])}
                                >
                                    select all
                                </button>
                                {scopeGroups.reads.map((scope) => (
                                    <ScopePill
                                        key={scope}
                                        scope={scope}
                                        selected={selectedScopes.includes(scope)}
                                        onToggle={toggleScope}
                                    />
                                ))}
                            </div>
                            <div className="col-sm-4">
                                <label>Can change <small className="text-muted">(create/update/delete)</small></label>{' '}
                                <button
                                    type="button"
                                    className="btn btn-link btn-xs"
                                    onClick={() => setSelectedScopes((current) => [...new Set([...current, ...scopeGroups.writes])])}
                                >
                                    select all
                                </button>
                                {scopeGroups.writes.map((scope) => (
                                    <ScopePill
                                        key={scope}
                                        scope={scope}
                                        selected={selectedScopes.includes(scope)}
                                        onToggle={toggleScope}
                                    />
                                ))}
                            </div>
                        </div>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={creating || !label || selectedScopes.length === 0}
                        >
                            {creating ? 'Creating…' : `Create connection${selectedScopes.length ? ` (${selectedScopes.length} permission${selectedScopes.length > 1 ? 's' : ''})` : ''}`}
                        </button>{' '}
                        {selectedScopes.length > 0 && (
                            <button type="button" className="btn btn-default" onClick={() => setSelectedScopes([])}>
                                Clear selection
                            </button>
                        )}
                        <span className="help-block" style={{ marginTop: 8 }}>
                            {selectedScopes.length === 0
                                ? 'Click the permissions above to choose what this assistant may do — at least one is required.'
                                : 'Only permissions you hold yourself are shown. The assistant can never do more than you can.'}
                        </span>
                    </form>

                    {newToken && (
                        <div className="alert alert-success" style={{ marginTop: 16 }}>
                            <strong>Connection created — copy the token now, it is not shown again.</strong>
                            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', userSelect: 'all', marginTop: 8 }}>
                                {newToken.access_token}
                            </pre>
                            <button type="button" className="btn btn-default btn-sm" onClick={() => copy(newToken.access_token, 'token')}>
                                {copied === 'token' ? '✓ Copied!' : 'Copy token'}
                            </button>{' '}
                            <button
                                type="button"
                                className="btn btn-default btn-sm"
                                onClick={() => copy(clientConfigJson(newToken), 'config')}
                            >
                                {copied === 'config' ? '✓ Copied!' : 'Copy client config (JSON)'}
                            </button>
                            {copied === 'failed' && (
                                <span className="text-danger" style={{ marginLeft: 8 }}>
                                    Clipboard blocked by the browser — select the text below and copy manually.
                                </span>
                            )}
                            <details style={{ marginTop: 8 }}>
                                <summary style={{ cursor: 'pointer' }}>Show client config (JSON)</summary>
                                <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', userSelect: 'all', marginTop: 6 }}>
                                    {clientConfigJson(newToken)}
                                </pre>
                            </details>
                        </div>
                    )}
                </div>
            </div>

            <div className="panel panel-default with-rounded-corner">
                <div className="panel-heading"><h4>Active connections</h4></div>
                <div className="panel-body">
                    {loading && <p>Loading…</p>}
                    {!loading && connections.length === 0 && <p>No AI connections yet.</p>}
                    {!loading && connections.length > 0 && (
                        <table className="table table-striped">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Scopes</th>
                                    <th>Created</th>
                                    <th>Expires</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {connections.map((connection) => (
                                    <React.Fragment key={connection.token_id}>
                                        <tr className={connection.expired ? 'text-muted' : ''}>
                                            <td>{connection.label}{connection.expired ? ' (expired)' : ''}</td>
                                            <td style={{ maxWidth: 320 }}>
                                                {(connection.scopes ?? []).map((scope) => (
                                                    <span key={scope} className="label label-default" style={{ marginRight: 4, display: 'inline-block', marginBottom: 2 }}>
                                                        {scopeLabel(scope)}
                                                    </span>
                                                ))}
                                            </td>
                                            <td>{connection.created_at ? new Date(connection.created_at).toLocaleDateString() : '—'}</td>
                                            <td>{connection.expires_at ? new Date(connection.expires_at).toLocaleDateString() : 'never'}</td>
                                            <td className="text-right" style={{ whiteSpace: 'nowrap' }}>
                                                <button type="button" className="btn btn-default btn-xs" onClick={() => showActivity(connection.token_id)}>
                                                    {activityFor === connection.token_id ? 'Hide activity' : 'Activity'}
                                                </button>{' '}
                                                <button type="button" className="btn btn-danger btn-xs" onClick={() => revoke(connection.token_id)}>
                                                    Revoke
                                                </button>
                                            </td>
                                        </tr>
                                        {activityFor === connection.token_id && (
                                            <tr>
                                                <td colSpan={5}>
                                                    {activity.length === 0 && <em>No recorded activity for this connection yet.</em>}
                                                    {activity.length > 0 && (
                                                        <table className="table table-condensed" style={{ marginBottom: 0 }}>
                                                            <thead>
                                                                <tr><th>When</th><th>Action</th><th>Path</th><th>Status</th><th>ms</th></tr>
                                                            </thead>
                                                            <tbody>
                                                                {activity.map((row, index) => (
                                                                    <tr key={index}>
                                                                        <td>{row.created_at ? new Date(row.created_at).toLocaleString() : ''}</td>
                                                                        <td>{row.route || row.method}</td>
                                                                        <td>{row.path}</td>
                                                                        <td>{row.status ?? ''}</td>
                                                                        <td>{row.duration_ms ?? ''}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    )}
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
}
